import { CarId } from "./protocol";
import type { Stage2RaceSituation, Stage2RelativeContext } from "./stage2-telemetry-data";

export interface ConversationIdentity {
  carId: CarId;
  raceTimeMs: number;
  seekGeneration: number;
}

export type ConversationInputMode = "microphone" | "text" | "automatic";
export type ConversationLanguage = "ja-JP" | "en-US" | "es-ES" | "fr-FR" | "zh-CN";

export const CONVERSATION_LANGUAGES: readonly {
  locale: ConversationLanguage;
  label: string;
  htmlLang: string;
}[] = [
  { locale: "ja-JP", label: "日本語", htmlLang: "ja" },
  { locale: "en-US", label: "English", htmlLang: "en" },
  { locale: "es-ES", label: "Español", htmlLang: "es" },
  { locale: "fr-FR", label: "Français", htmlLang: "fr" },
  { locale: "zh-CN", label: "中文", htmlLang: "zh-Hans" },
];

export function speechSegments(value: string, locale: ConversationLanguage): string[] {
  const normalized = value.trim().replace(/\s+/gu, " ");
  if (!normalized) return [];
  const maximumCharacters = locale === "ja-JP" || locale === "zh-CN" ? 48 : 140;
  const sentences = normalized.match(/[^。！？.!?]+(?:[。！？.!?]+|$)/gu) ?? [normalized];
  const output: string[] = [];
  for (const sentenceValue of sentences) {
    let remaining = sentenceValue.trim();
    while ([...remaining].length > maximumCharacters) {
      const characters = [...remaining];
      const candidate = characters.slice(0, maximumCharacters);
      let splitAt = -1;
      for (let index = candidate.length - 1; index >= Math.floor(maximumCharacters / 2); index -= 1) {
        if (/[、，,；;：:\s]/u.test(candidate[index])) {
          splitAt = index + 1;
          break;
        }
      }
      if (splitAt < 0) splitAt = maximumCharacters;
      output.push(candidate.slice(0, splitAt).join("").trim());
      remaining = characters.slice(splitAt).join("").trim();
    }
    if (remaining) output.push(remaining);
  }
  return output;
}

export function firstSentence(value: string): string {
  const normalized = value.trim().replace(/\s+/gu, " ");
  if (!normalized) return "";
  const match = normalized.match(/^.*?(?:[。！？!?]+|[.]+(?=\s|$))/u);
  return (match?.[0] ?? normalized).trim();
}

export const AUTOMATIC_USER_CAPTION_MINIMUM_MS = 2_000;
export const AUTOMATIC_USER_CAPTION_MAXIMUM_MS = 5_200;

export function automaticUserCaptionDurationMs(value: string): number {
  const normalized = value.trim().replace(/\s+/gu, " ");
  const characterCount = [...normalized].length;
  const punctuationCount = normalized.match(/[、。！？!?]/gu)?.length ?? 0;
  return Math.min(
    AUTOMATIC_USER_CAPTION_MAXIMUM_MS,
    Math.max(
      AUTOMATIC_USER_CAPTION_MINIMUM_MS,
      700 + characterCount * 180 + punctuationCount * 220,
    ),
  );
}

export interface SixPseudoIndicators {
  DA: number;
  OP: number;
  NA: number;
  Ach: number;
  VP: number;
  "5-HT": number;
}

export interface EmotionMapContext {
  situation: { cell_id: string; value: number }[];
  reaction: { cell_id: string; value: number }[];
}

export interface ConversationEmotionNode {
  cell_id: number;
  label: string;
  layer: number;
  activation: number;
  delta: number | null;
  trend: "rising" | "steady" | "falling" | "unknown";
}

export interface ConversationEmotionIndividual {
  active_nodes: ConversationEmotionNode[];
  source_balance: {
    situation: number;
    reaction: number;
    dominant: "situation" | "reaction" | "balanced";
  };
}

export interface ConversationContext {
  schema_version: "stage2-replay-conversation-context/v1";
  car_id: CarId;
  race_time_ms: number;
  seek_generation: number;
  driver_profile: { name_ja: string; car_number: string };
  telemetry: { speed_kmh: number; rpm: number };
  operations: {
    steering_deg: number;
    throttle_raw: number;
    front_brake_pressure: number;
    gear: number;
  };
  vehicle_response: { turn_rad_s: number; drive_g: number; brake_g: number };
  relative_context: Stage2RelativeContext;
  race_situation: Stage2RaceSituation | null;
  synchronization: {
    rate_percent: number;
    raw_rate_percent: number;
    baseline_percent: 50;
    interpretation: "same_direction" | "partly_aligned" | "neutral" | "opposed";
    source: "driver_and_vehicle_emotion_maps";
  };
  pseudo_indicators: {
    driver: SixPseudoIndicators;
    vehicle: SixPseudoIndicators;
  };
  pseudo_indicator_relative: {
    driver: Record<keyof SixPseudoIndicators, { current: number; band: string; interpretation: string }>;
    vehicle: Record<keyof SixPseudoIndicators, { current: number; band: string; interpretation: string }>;
  };
  emotion_map: {
    driver: EmotionMapContext;
    vehicle: EmotionMapContext;
  };
  emotion_state: {
    causal_id: string;
    tick_index: number;
    race_time_ms: number;
    delta_interval_ms: number | null;
    driver: ConversationEmotionIndividual;
    vehicle: ConversationEmotionIndividual;
  };
  race_events: readonly unknown[];
  active_story: readonly { evidence_id: string; text_ja: string }[];
}

export interface ConversationMessage {
  role: "user" | "assistant";
  text: string;
}

export type ConversationHistoryMessage = ConversationMessage;

export interface ConversationRequest {
  schema_version: "stage2-vehicle-ai-request/v1";
  request_id: string;
  conversation_flow: { messages: ConversationMessage[] };
  conversation_metadata: {
    car_id: CarId;
    current_race_time_ms: number;
    seek_generation: number;
    locale: ConversationLanguage;
    input_mode: ConversationInputMode;
  };
  race_context: ConversationContext;
}

export interface ConversationAudioRequest {
  schema_version: "stage2-vehicle-ai-audio-request/v1";
  request_id: string;
  audio: {
    encoding: "wav";
    sample_rate_hz: 16000;
    channels: 1;
    base64: string;
  };
  conversation_flow: { messages: ConversationMessage[] };
  conversation_metadata: {
    car_id: CarId;
    current_race_time_ms: number;
    seek_generation: number;
    locale: ConversationLanguage;
    input_mode: "microphone";
  };
  race_context: ConversationContext;
}

export interface ConversationTranscription {
  schema_version: "stage2-vehicle-ai-transcription/v1";
  request_id: string;
  car_id: CarId;
  race_time_ms: number;
  seek_generation: number;
  locale: ConversationLanguage;
  text: string;
}

export interface ConversationSpeechRequest {
  schema_version: "stage2-vehicle-ai-speech-request/v1";
  request_id: string;
  response_id: string;
  car_id: CarId;
  race_time_ms: number;
  seek_generation: number;
  locale: ConversationLanguage;
  text: string;
  segment_index?: number;
  segment_count?: number;
}

export interface ConversationSpeechReply {
  schema_version: "stage2-vehicle-ai-speech/v1";
  request_id: string;
  response_id: string;
  car_id: CarId;
  race_time_ms: number;
  seek_generation: number;
  segment_index?: number;
  segment_count?: number;
  speech: ConversationReply["speech"];
}

export interface ConversationReply {
  schema_version: "stage2-vehicle-ai-response/v1";
  request_id: string;
  response_id: string;
  car_id: CarId;
  race_time_ms: number;
  seek_generation: number;
  utterance_echo?: string;
  answer_text: string;
  subtitle: {
    speaker_label: string;
    text: string;
  };
  speech: {
    mode: string;
    locale: string;
    text: string;
    audio_mime_type?: string | null;
    audio_base64?: string | null;
    error?: { code: string; message: string } | null;
  };
}

export interface ConversationAdapter {
  requestReply(request: ConversationRequest, signal?: AbortSignal): Promise<ConversationReply>;
  requestTranscription(request: ConversationAudioRequest, signal?: AbortSignal): Promise<ConversationTranscription>;
  requestSpeech(request: ConversationSpeechRequest, signal?: AbortSignal): Promise<ConversationSpeechReply>;
}

export function sameConversationIdentity(
  left: ConversationIdentity,
  right: ConversationIdentity,
): boolean {
  return left.carId === right.carId
    && left.raceTimeMs === right.raceTimeMs
    && left.seekGeneration === right.seekGeneration;
}

// The race clock normally advances while a reply is generated.  A reply still
// belongs to the displayed owner until a car switch or a seek changes it.
export function sameConversationOwner(
  left: ConversationIdentity,
  right: ConversationIdentity,
): boolean {
  return left.carId === right.carId && left.seekGeneration === right.seekGeneration;
}

export function replyIdentity(reply: ConversationReply): ConversationIdentity {
  return {
    carId: reply.car_id,
    raceTimeMs: reply.race_time_ms,
    seekGeneration: reply.seek_generation,
  };
}

export function responseIdentity(
  response: ConversationReply | ConversationTranscription | ConversationSpeechReply,
): ConversationIdentity {
  return {
    carId: response.car_id,
    raceTimeMs: response.race_time_ms,
    seekGeneration: response.seek_generation,
  };
}

export function buildConversationRequest(
  identity: ConversationIdentity,
  text: string,
  inputMode: ConversationInputMode,
  context: ConversationContext,
  requestId: string,
  conversationHistory: ConversationHistoryMessage[] = [],
  locale: ConversationLanguage = "ja-JP",
): ConversationRequest {
  const normalizedHistory = conversationHistory
    .filter(message => (message.role === "user" || message.role === "assistant") && message.text.trim())
    .map(message => ({ ...message, text: message.text.trim() }));
  const flow = [...normalizedHistory.slice(-6), { role: "user" as const, text: text.trim() }];
  return {
    schema_version: "stage2-vehicle-ai-request/v1",
    request_id: requestId,
    conversation_flow: { messages: flow },
    conversation_metadata: {
      car_id: identity.carId,
      current_race_time_ms: identity.raceTimeMs,
      seek_generation: identity.seekGeneration,
      locale,
      input_mode: inputMode,
    },
    race_context: {
      ...context,
      schema_version: "stage2-replay-conversation-context/v1",
      car_id: identity.carId,
      race_time_ms: identity.raceTimeMs,
      seek_generation: identity.seekGeneration,
    },
  };
}

export function vehicleNumber(carId: CarId): string {
  return carId === "car1" ? "1" : carId === "car6" ? "6" : "14";
}

export function userCaption(text: string, locale: ConversationLanguage = "ja-JP"): string {
  const label = locale === "en-US" ? "You"
    : locale === "es-ES" ? "Tú"
      : locale === "fr-FR" ? "Vous"
        : locale === "zh-CN" ? "用户" : "ユーザー";
  return `${label}「${text.trim()}」`;
}

export function vehicleAiCaption(
  carId: CarId,
  text: string,
  locale: ConversationLanguage = "ja-JP",
): string {
  const number = vehicleNumber(carId);
  const label = locale === "en-US" ? `Car ${number} AI`
    : locale === "es-ES" ? `IA del coche ${number}`
      : locale === "fr-FR" ? `IA de la voiture ${number}`
        : locale === "zh-CN" ? `${number}号车AI` : `${number}号車AI`;
  return `${label}「${text.trim()}」`;
}

export function isConversationLanguage(value: unknown): value is ConversationLanguage {
  return value === "ja-JP" || value === "en-US" || value === "es-ES"
    || value === "fr-FR" || value === "zh-CN";
}

function isCarId(value: unknown): value is CarId {
  return value === "car1" || value === "car6" || value === "car14";
}

export function isConversationReply(value: unknown): value is ConversationReply {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<ConversationReply>;
  const subtitle = item.subtitle as Partial<ConversationReply["subtitle"]> | undefined;
  const speech = item.speech as Partial<ConversationReply["speech"]> | undefined;
  return item.schema_version === "stage2-vehicle-ai-response/v1"
    && typeof item.request_id === "string" && item.request_id.length > 0
    && typeof item.response_id === "string" && item.response_id.length > 0
    && isCarId(item.car_id)
    && Number.isInteger(item.race_time_ms) && Number(item.race_time_ms) >= 0
    && Number.isInteger(item.seek_generation) && Number(item.seek_generation) >= 0
    && typeof item.answer_text === "string" && item.answer_text.trim().length > 0
    && !!subtitle && typeof subtitle.speaker_label === "string" && typeof subtitle.text === "string"
    && !!speech && typeof speech.mode === "string" && isConversationLanguage(speech.locale)
    && typeof speech.text === "string";
}

export function isConversationTranscription(value: unknown): value is ConversationTranscription {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<ConversationTranscription>;
  return item.schema_version === "stage2-vehicle-ai-transcription/v1"
    && typeof item.request_id === "string" && item.request_id.length > 0
    && isCarId(item.car_id)
    && Number.isInteger(item.race_time_ms) && Number(item.race_time_ms) >= 0
    && Number.isInteger(item.seek_generation) && Number(item.seek_generation) >= 0
    && isConversationLanguage(item.locale)
    && typeof item.text === "string" && item.text.trim().length > 0;
}

export function isConversationSpeechReply(value: unknown): value is ConversationSpeechReply {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<ConversationSpeechReply>;
  const speech = item.speech as Partial<ConversationReply["speech"]> | undefined;
  return item.schema_version === "stage2-vehicle-ai-speech/v1"
    && typeof item.request_id === "string" && item.request_id.length > 0
    && typeof item.response_id === "string" && item.response_id.length > 0
    && isCarId(item.car_id)
    && Number.isInteger(item.race_time_ms) && Number(item.race_time_ms) >= 0
    && Number.isInteger(item.seek_generation) && Number(item.seek_generation) >= 0
    && !!speech && typeof speech.mode === "string" && isConversationLanguage(speech.locale)
    && typeof speech.text === "string";
}

export class HttpConversationAdapter implements ConversationAdapter {
  constructor(
    private readonly endpoint = "/api/conversation/respond",
    private readonly transcriptionEndpoint = "/api/conversation/transcribe",
    private readonly speechEndpoint = "/api/conversation/synthesize",
  ) {}

  private async post(
    endpoint: string,
    request: ConversationRequest | ConversationAudioRequest | ConversationSpeechRequest,
    signal?: AbortSignal,
  ): Promise<unknown> {
    const locale = "conversation_metadata" in request ? request.conversation_metadata.locale : request.locale;
    const response = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request),
      signal,
    });
    if (!response.ok) {
      const fallback = {
        "ja-JP": `音声対話を利用できません（HTTP ${response.status}）。`,
        "en-US": `Voice conversation is unavailable (HTTP ${response.status}).`,
        "es-ES": `La conversación de voz no está disponible (HTTP ${response.status}).`,
        "fr-FR": `La conversation vocale est indisponible (HTTP ${response.status}).`,
        "zh-CN": `语音对话不可用（HTTP ${response.status}）。`,
      }[locale];
      let message = fallback;
      try {
        const problem = await response.json() as { title?: unknown };
        if (typeof problem.title === "string" && problem.title) message = problem.title;
      } catch { /* The status-based message remains useful. */ }
      throw new Error(message);
    }
    return response.json();
  }

  async requestReply(request: ConversationRequest, signal?: AbortSignal): Promise<ConversationReply> {
    const value = await this.post(this.endpoint, request, signal);
    if (!isConversationReply(value)) throw new Error("conversation reply schema differs");
    return value;
  }

  async requestTranscription(
    request: ConversationAudioRequest,
    signal?: AbortSignal,
  ): Promise<ConversationTranscription> {
    const value = await this.post(this.transcriptionEndpoint, request, signal);
    if (!isConversationTranscription(value)) throw new Error("conversation transcription schema differs");
    return value;
  }

  async requestSpeech(request: ConversationSpeechRequest, signal?: AbortSignal): Promise<ConversationSpeechReply> {
    const value = await this.post(this.speechEndpoint, request, signal);
    if (!isConversationSpeechReply(value)) throw new Error("conversation speech schema differs");
    return value;
  }
}

function writeAscii(view: DataView, offset: number, value: string): void {
  for (let index = 0; index < value.length; index += 1) view.setUint8(offset + index, value.charCodeAt(index));
}

export function encodeMonoFloat32Wav(samples: Float32Array, sampleRate = 16_000): Uint8Array {
  const bytes = new Uint8Array(44 + samples.length * 2);
  const view = new DataView(bytes.buffer);
  writeAscii(view, 0, "RIFF");
  view.setUint32(4, bytes.byteLength - 8, true);
  writeAscii(view, 8, "WAVE");
  writeAscii(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeAscii(view, 36, "data");
  view.setUint32(40, samples.length * 2, true);
  samples.forEach((sample, index) => {
    const clipped = Math.max(-1, Math.min(1, sample));
    view.setInt16(44 + index * 2, clipped < 0 ? clipped * 0x8000 : clipped * 0x7fff, true);
  });
  return bytes;
}

export function bytesToBase64(bytes: Uint8Array): string {
  let result = "";
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    result += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return btoa(result);
}
